/**
 * Notas por plantilla (SDD §7.2, §9.4): versiones, regeneración con límites,
 * edición (cada guardado crea una versión `editada`), corrección por chat con los
 * cambios marcados, «¿te sirvieron?» y evidencia que salta al momento exacto de la
 * transcripción.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  STANDARD_TEMPLATE_IDS,
  TEMPLATES,
  speakerDisplayName,
  type CustomTemplate,
  type ActionItem,
  type CorrectionTurn,
  type RecordingDetail,
  type Summary,
  type TemplateId
} from '@eco/shared'
import { useApp, useToast, errorText } from './AppContext'
import { Modal } from './ui'
import { formatDate, formatDueDate, formatDuration } from '../lib/format'
import { notesMarkdown } from '../lib/exportMarkdown'
import {
  editableFields,
  linesOf,
  noteSections,
  setLines,
  setTasks,
  setText,
  taskOwner,
  tasksOf,
  templateName
} from '../lib/notes'
import { useAsync } from '../lib/hooks'
import { labelToName } from '../lib/transcript'
import { IconClose, IconThumb } from './icons'

interface Props {
  detail: RecordingDetail
  onJump: (ms: number) => void
  onChanged: () => Promise<void> | void
  /** Reunión de otra persona del equipo: se lee, no se edita ni se regenera. */
  readOnly?: boolean
}

/**
 * Pestaña de notas: una por plantilla de eco y una por cada plantilla
 * personalizada usada ('personalizada:<id>'; sin id si se borró).
 */
type TabKey = string
const keyOf = (s: Pick<Summary, 'templateId' | 'customTemplateId'>): TabKey =>
  s.templateId === 'personalizada' ? `personalizada:${s.customTemplateId ?? ''}` : s.templateId
function parseKey(k: TabKey): { templateId: TemplateId; customTemplateId?: string } {
  if (!k.startsWith('personalizada:')) return { templateId: k as TemplateId }
  return {
    templateId: 'personalizada',
    customTemplateId: k.slice('personalizada:'.length) || undefined
  }
}

function byNewest(a: Summary, b: Summary) {
  return b.version - a.version || (a.creadoEn < b.creadoEn ? 1 : -1)
}

export function NotesPanel({ detail, onJump, onChanged, readOnly = false }: Props) {
  const { api } = useApp()
  const toast = useToast()
  const summaries = detail.summaries
  const customs = useAsync(() => api.customTemplates().catch(() => [] as CustomTemplate[]), [api])
  const used = useMemo(() => {
    const keys: TabKey[] = []
    for (const s of [...summaries].sort((a, b) => a.creadoEn.localeCompare(b.creadoEn)))
      if (!keys.includes(keyOf(s))) keys.push(keyOf(s))
    return keys
  }, [summaries])
  const [tpl, setTpl] = useState<TabKey>(used[0] ?? 'reunion_general')
  useEffect(() => {
    if (!used.includes(tpl) && used[0]) setTpl(used[0])
  }, [used, tpl])
  const versions = useMemo(
    () => summaries.filter((s) => keyOf(s) === tpl).sort(byNewest),
    [summaries, tpl]
  )
  const tabName = (k: TabKey) => {
    const { templateId, customTemplateId } = parseKey(k)
    if (templateId !== 'personalizada') return TEMPLATES[templateId].nombre
    const latest = summaries.filter((s) => keyOf(s) === k).sort(byNewest)[0]
    return (
      customs.data?.find((c) => c.id === customTemplateId)?.nombre ??
      (latest ? templateName(latest) : 'Personalizada')
    )
  }
  const [versionId, setVersionId] = useState<string | null>(null)
  const current = versions.find((v) => v.id === versionId) ?? versions[0] ?? null
  useEffect(() => setVersionId(null), [tpl, versions.length])
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [downOpen, setDownOpen] = useState(false)
  const [motivo, setMotivo] = useState('')
  // Tras una corrección: con qué contenido comparar para marcar lo que cambió
  const [compare, setCompare] = useState<{ id: string; prev: unknown } | null>(null)
  const hasTranscript = detail.segments.length > 0
  const nameOf = (e: string) => labelToName(detail.speakers, e)

  async function generate(key: TabKey) {
    const { templateId, customTemplateId } = parseKey(key)
    if (templateId === 'personalizada' && !customTemplateId) {
      toast('Esa plantilla ya no existe: elige otra.', 'error')
      return
    }
    setBusy(true)
    try {
      await api.requestSummary(detail.recording.id, templateId, customTemplateId)
      // Primero recargar: si la pestaña cambia antes, el efecto la devuelve a la primera
      await onChanged()
      setTpl(key)
      toast('Escribiendo notas… tarda unos segundos.')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function feedback(value: 'up' | 'down' | null, m?: string) {
    if (!current) return
    try {
      await api.summaryFeedback(detail.recording.id, current.id, value, m)
      toast(
        value === 'up' ? '¡Gracias!' : value === 'down' ? 'Gracias, nos ayuda a mejorar.' : 'Listo'
      )
      await onChanged()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  function copyNotes() {
    if (!current) return
    const md = [
      `# ${detail.recording.titulo}`,
      '',
      ...notesMarkdown(current.templateId, current.contenido, nameOf)
    ].join('\n')
    void navigator.clipboard
      .writeText(md)
      .then(() => toast('Notas copiadas'))
      .catch(() => toast('No se pudo copiar. Revisa el permiso del portapapeles.', 'error'))
  }

  const otherTemplates: { key: TabKey; nombre: string }[] = [
    ...STANDARD_TEMPLATE_IDS.map((t) => ({ key: t as TabKey, nombre: TEMPLATES[t].nombre })),
    ...(customs.data ?? []).map((c) => ({
      key: `personalizada:${c.id}`,
      nombre: `${c.nombre} (tuya)`
    }))
  ].filter((o) => !used.includes(o.key))

  return (
    <div className="card">
      <div className="tabs">
        <div role="tablist" aria-label="Plantillas de notas" style={{ display: 'flex', gap: 4 }}>
          {used.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={t === tpl}
              className={`tab ${t === tpl ? 'on' : ''}`}
              onClick={() => {
                setTpl(t)
                setEditing(false)
              }}
            >
              {tabName(t)}
            </button>
          ))}
        </div>
        {!readOnly && hasTranscript && otherTemplates.length > 0 && (
          <select
            aria-label="Generar notas con otra plantilla"
            className="tab"
            value=""
            disabled={busy}
            onChange={(e) => e.target.value && void generate(e.target.value)}
            style={{ border: 0, background: 'transparent' }}
          >
            <option value="">+ Otra plantilla</option>
            {otherTemplates.map((o) => (
              <option key={o.key} value={o.key}>
                {o.nombre}
              </option>
            ))}
          </select>
        )}
      </div>

      {!current ? (
        <div className="muted">
          {hasTranscript ? (
            <div className="stack">
              <p>Aún no hay notas para esta reunión.</p>
              {!readOnly && (
                <button className="btn primary" disabled={busy} onClick={() => void generate(tpl)}>
                  Generar notas
                </button>
              )}
            </div>
          ) : (
            <p>Las notas aparecen cuando termine la transcripción.</p>
          )}
        </div>
      ) : current.estado === 'generating' ? (
        <div className="stack" aria-live="polite">
          <span className="pill work">
            <span className="dot" /> Escribiendo notas…
          </span>
          <p className="faint">Suele tardar menos de un minuto.</p>
        </div>
      ) : current.estado === 'failed' || current.estado === 'rechazado' ? (
        <div className="stack">
          <div className="notice warn">
            {current.estado === 'rechazado'
              ? 'El modelo no pudo escribir notas para esta conversación.'
              : 'No se pudieron generar las notas.'}
          </div>
          {!readOnly && (
            <button className="btn" disabled={busy} onClick={() => void generate(tpl)}>
              Intentar de nuevo
            </button>
          )}
        </div>
      ) : editing ? (
        <NotesEditor
          summary={current}
          detail={detail}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false)
            toast('Notas guardadas como nueva versión')
            await onChanged()
          }}
        />
      ) : (
        <>
          <div className="row wrap no-print" style={{ marginBottom: 12 }}>
            {versions.length > 1 && (
              <select
                aria-label="Versión"
                value={current.id}
                onChange={(e) => setVersionId(e.target.value)}
              >
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version}
                    {v.estado === 'editada' ? ' (editada)' : ''} · {formatDate(v.creadoEn)}
                  </option>
                ))}
              </select>
            )}
            <span className="grow" />
            <button className="btn sm ghost" onClick={copyNotes}>
              Copiar
            </button>
            {!readOnly && (
              <>
                <button className="btn sm ghost" onClick={() => setEditing(true)}>
                  Editar
                </button>
                <button
                  className="btn sm ghost"
                  disabled={busy}
                  onClick={() => void generate(tpl)}
                  title="Crea una nueva versión"
                >
                  Regenerar
                </button>
              </>
            )}
          </div>
          <NotesView
            templateId={current.templateId}
            contenido={current.contenido}
            onJump={onJump}
            nameOf={nameOf}
            compareTo={compare?.id === current.id ? compare.prev : undefined}
          />
          <div className="row no-print" style={{ marginTop: 10, fontSize: 12.5 }}>
            <span className="faint grow">¿Te sirvieron estas notas?</span>
            <button
              className={`btn sm ${current.feedback === 'up' ? '' : 'ghost'}`}
              aria-pressed={current.feedback === 'up'}
              aria-label="Sí, me sirvieron"
              onClick={() => void feedback(current.feedback === 'up' ? null : 'up')}
            >
              <IconThumb /> Sí
            </button>
            <button
              className={`btn sm ${current.feedback === 'down' ? '' : 'ghost'}`}
              aria-pressed={current.feedback === 'down'}
              aria-label="No me sirvieron"
              onClick={() =>
                current.feedback === 'down' ? void feedback(null) : setDownOpen(true)
              }
            >
              <IconThumb style={{ transform: 'scaleY(-1)' }} /> No
            </button>
          </div>
          {!readOnly && (
            <CorrectPanel
              key={tpl}
              recordingId={detail.recording.id}
              current={current}
              onCorrected={async (nueva, prev) => {
                await onChanged()
                setVersionId(nueva.id)
                setCompare(prev === null ? null : { id: nueva.id, prev })
              }}
            />
          )}
        </>
      )}

      <Modal
        open={downOpen}
        onClose={() => setDownOpen(false)}
        title="¿Qué salió mal?"
        footer={
          <>
            <button className="btn ghost" onClick={() => setDownOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn primary"
              onClick={() => {
                setDownOpen(false)
                void feedback('down', motivo.trim() || undefined)
                setMotivo('')
              }}
            >
              Enviar
            </button>
          </>
        }
      >
        <label className="field">
          <span>Opcional: cuéntanos qué faltó o qué estaba mal</span>
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} />
        </label>
      </Modal>
    </div>
  )
}

export function NotesView({
  templateId,
  contenido,
  onJump,
  nameOf = (e) => e,
  compareTo
}: {
  templateId: TemplateId
  contenido: unknown
  onJump?: (ms: number) => void
  nameOf?: (etiqueta: string) => string
  /** Versión anterior: lo que no estaba en ella se marca como corregido. */
  compareTo?: unknown
}) {
  const sections = noteSections(templateId, contenido, nameOf)
  const changed = useMemo(
    () => (compareTo === undefined ? null : changedParts(templateId, compareTo, nameOf)),
    [templateId, compareTo, nameOf]
  )
  const chg = (on: boolean) => (on ? 'chg' : undefined)
  const Ev = ({ ms }: { ms: number }) =>
    onJump ? (
      <button className="ev" onClick={() => onJump(ms)} title="Ver en la transcripción">
        {formatDuration(ms)}
      </button>
    ) : (
      <span className="ev">{formatDuration(ms)}</span>
    )
  return (
    <div className="notes">
      {sections.map((s) => (
        <section key={s.key}>
          <h3>{s.title}</h3>
          {s.kind === 'text' && (
            <p className={chg(!!changed && !changed.has(`${s.key}:${s.text}`))}>
              {s.text || <span className="faint">(vacío)</span>}
            </p>
          )}
          {s.kind === 'flag' && (
            <p className={chg(!!changed && !changed.has(`${s.key}:${s.label}`))}>{s.label}</p>
          )}
          {s.kind === 'list' && (
            <ul>
              {s.items.map((it, i) => (
                <li key={i}>
                  <span className={chg(!!changed && !changed.has(`${s.key}:${it.texto}`))}>
                    {it.texto}
                  </span>
                  {it.evidencia.slice(0, 2).map((e, j) => (
                    <Ev key={j} ms={e.inicioMs} />
                  ))}
                </li>
              ))}
            </ul>
          )}
          {s.kind === 'tasks' && (
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {s.items.map((t, i) => {
                const owner = taskOwner(t, nameOf)
                return (
                  <li key={i} className="task">
                    <span className="box" aria-hidden="true" />
                    <span>
                      <span className={chg(!!changed && !changed.has(taskKey(t, owner)))}>
                        {t.descripcion}
                      </span>
                      {(owner || t.fecha) && (
                        <span className="who">
                          {' '}
                          — {[owner, t.fecha && formatDueDate(t.fecha)].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {t.evidencia.slice(0, 1).map((e, j) => (
                        <Ev key={j} ms={e.inicioMs} />
                      ))}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      ))}
    </div>
  )
}

/** Clave de una tarea para comparar versiones: texto, responsable y fecha. */
function taskKey(t: ActionItem, owner: string | null): string {
  return `tasks:${t.descripcion}|${owner ?? ''}|${t.fecha ?? ''}`
}

/** Lo que había en la versión anterior, como claves comparables (ver `NotesView`). */
function changedParts(
  templateId: TemplateId,
  prev: unknown,
  nameOf: (e: string) => string
): Set<string> {
  const set = new Set<string>()
  for (const s of noteSections(templateId, prev, nameOf)) {
    if (s.kind === 'text') set.add(`${s.key}:${s.text}`)
    else if (s.kind === 'flag') set.add(`${s.key}:${s.label}`)
    else if (s.kind === 'list') for (const it of s.items) set.add(`${s.key}:${it.texto}`)
    else for (const t of s.items) set.add(taskKey(t, taskOwner(t, nameOf)))
  }
  return set
}

interface CorrectTurn extends CorrectionTurn {
  /** Versión que creó esta corrección y el contenido anterior (para deshacer). */
  versionId: string | null
  prev: unknown
  terminos: { dicho: string; correcto: string }[]
  deshecha?: boolean
}

const EJEMPLOS = [
  'No es Juan, es Juana',
  'El plazo es el 15, no el 5',
  'Eso no se decidió, quedó pendiente'
]

/**
 * Chat para corregir las notas: cada mensaje crea una versión editada con los
 * cambios marcados. La conversación vive solo mientras la página está abierta.
 */
function CorrectPanel({
  recordingId,
  current,
  onCorrected
}: {
  recordingId: string
  current: Summary
  /** `prev` null: sin marcas (al deshacer). */
  onCorrected: (nueva: Summary, prev: unknown) => Promise<void>
}) {
  const { api } = useApp()
  const toast = useToast()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [turns, setTurns] = useState<CorrectTurn[]>([])
  const [restantes, setRestantes] = useState<number | null>(null)
  const [aprendidos, setAprendidos] = useState<string[]>([])
  const agotadas = restantes === 0

  async function send(instruccion: string) {
    const t = instruccion.trim()
    if (t.length < 3 || busy || agotadas) return
    setBusy(true)
    // Las últimas 5 aplicadas, de la más antigua a la más nueva
    const historial = turns
      .filter((x) => !x.deshecha)
      .slice(0, 5)
      .reverse()
      .map((x) => ({ instruccion: x.instruccion, respuesta: x.respuesta.slice(0, 500) }))
    try {
      const r = await api.correctSummary(recordingId, current.id, t, historial)
      setTurns((prev) => [
        {
          instruccion: t,
          respuesta: r.respuesta,
          versionId: r.summary?.id ?? null,
          prev: current.contenido,
          terminos: r.terminos
        },
        ...prev
      ])
      setRestantes(r.correccionesRestantes)
      setText('')
      if (r.summary) await onCorrected(r.summary, current.contenido)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function undo(turn: CorrectTurn) {
    if (!turn.versionId) return
    setBusy(true)
    try {
      const nueva = await api.editSummary(recordingId, current.id, turn.prev)
      setTurns((prev) => prev.map((x) => (x === turn ? { ...x, deshecha: true } : x)))
      await onCorrected(nueva, null)
      toast('Corrección deshecha')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function learn(term: string) {
    try {
      const { terminos } = await api.vocabulary()
      if (!terminos.some((x) => x.toLowerCase() === term.toLowerCase()))
        await api.setVocabulary([...terminos, term])
      setAprendidos((a) => [...a, term])
      toast(`«${term}» quedó en tu vocabulario: las próximas reuniones lo escribirán bien.`)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  // Solo se deshace la última corrección aplicada, y si las notas siguen en esa versión
  const ultima = turns.find((x) => x.versionId && !x.deshecha)

  return (
    <div className="stack no-print correct" style={{ gap: 10 }}>
      <div className="row">
        <h3 className="grow" style={{ fontSize: 15 }}>
          ¿Algo quedó mal?
        </h3>
        {restantes !== null && (
          <span className="faint" style={{ fontSize: 11.5 }}>
            {restantes === 0
              ? 'Sin correcciones por hoy'
              : `${restantes} ${restantes === 1 ? 'corrección' : 'correcciones'} más hoy`}
          </span>
        )}
      </div>
      {turns.length > 0 && (
        <div className="stack" style={{ gap: 8 }} aria-live="polite">
          {[...turns].reverse().map((t, i) => (
            <div key={i} className="stack" style={{ gap: 4 }}>
              <div className="bubble me">{t.instruccion}</div>
              <div className={`bubble ${t.deshecha ? 'faint' : ''}`}>
                {t.deshecha ? <s>{t.respuesta}</s> : t.respuesta}
                {t === ultima && t.versionId === current.id && (
                  <button
                    className="btn sm ghost"
                    style={{ marginLeft: 8 }}
                    disabled={busy}
                    onClick={() => void undo(t)}
                  >
                    Deshacer
                  </button>
                )}
              </div>
              {!t.deshecha &&
                t.terminos
                  .filter((x) => !aprendidos.includes(x.correcto))
                  .map((x) => (
                    <div key={x.correcto} className="row wrap faint" style={{ fontSize: 12.5 }}>
                      <span className="grow">
                        La transcripción escribió «{x.dicho}». ¿Lo aprendo como «{x.correcto}» para
                        las próximas reuniones?
                      </span>
                      <button className="btn sm" onClick={() => void learn(x.correcto)}>
                        Agregar al vocabulario
                      </button>
                    </div>
                  ))}
            </div>
          ))}
        </div>
      )}
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          void send(text)
        }}
      >
        <input
          className="grow"
          aria-label="Corrección para las notas"
          placeholder="Dime qué está mal: «no es Juan, es Juana»"
          value={text}
          maxLength={1000}
          onChange={(e) => setText(e.target.value)}
          readOnly={busy}
          disabled={agotadas}
          aria-busy={busy}
        />
        <button className="btn" disabled={busy || agotadas || text.trim().length < 3}>
          {busy ? 'Corrigiendo…' : 'Corregir'}
        </button>
      </form>
      {turns.length === 0 && (
        <div className="row wrap" style={{ gap: 6 }}>
          {EJEMPLOS.map((x) => (
            <button key={x} className="chip" onClick={() => setText(x)} disabled={busy}>
              {x}
            </button>
          ))}
        </div>
      )}
      <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>
        Cada corrección guarda una versión nueva de las notas. La conversación no se guarda.
      </p>
    </div>
  )
}

function NotesEditor({
  summary,
  detail,
  onCancel,
  onSaved
}: {
  summary: Summary
  detail: RecordingDetail
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const { api } = useApp()
  const toast = useToast()
  const [draft, setDraft] = useState<unknown>(summary.contenido)
  const [saving, setSaving] = useState(false)
  const fields = editableFields(summary.templateId, summary.contenido)
  const etiquetas = useMemo(() => {
    const set = new Set<string>()
    for (const s of detail.speakers) set.add(speakerDisplayName(s.etiqueta))
    return [...set]
  }, [detail.speakers])

  async function save() {
    setSaving(true)
    try {
      await api.editSummary(detail.recording.id, summary.id, draft)
      await onSaved()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stack">
      {fields.map((f) => {
        const value = (draft as Record<string, unknown>)?.[f.key]
        if (f.kind === 'text')
          return (
            <label key={f.key} className="field">
              <span>{f.title}</span>
              <textarea
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => setDraft(setText(draft, f.key, e.target.value))}
                rows={f.key === 'resumen' ? 5 : 3}
              />
            </label>
          )
        if (f.kind === 'flag')
          return (
            <label key={f.key} className="field">
              <span>{f.title}</span>
              <select
                value={typeof value === 'string' ? value : 'no_consta'}
                onChange={(e) => setDraft({ ...(draft as object), [f.key]: e.target.value })}
              >
                {Object.entries(f.labels).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          )
        if (f.kind === 'tasks')
          return (
            <TasksEditor
              key={f.key}
              title={f.title}
              tasks={tasksOf(draft)}
              etiquetas={etiquetas}
              nameOf={(e) => labelToName(detail.speakers, e)}
              onChange={(t) => setDraft(setTasks(draft, t))}
            />
          )
        return (
          <LinesField
            key={f.key}
            title={f.title}
            initial={linesOf(draft, f.key)}
            onChange={(lines) =>
              setDraft((d: unknown) => setLines(summary.templateId, d, f.key, lines))
            }
          />
        )
      })}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={onCancel} disabled={saving}>
          Cancelar
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}

function LinesField({
  title,
  initial,
  onChange
}: {
  title: string
  initial: string[]
  onChange: (lines: string[]) => void
}) {
  const [raw, setRaw] = useState(initial.join('\n'))
  return (
    <label className="field">
      <span>{title} (uno por línea)</span>
      <textarea
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value)
          onChange(e.target.value.split('\n'))
        }}
        rows={Math.max(3, raw.split('\n').length + 1)}
      />
    </label>
  )
}

function TasksEditor({
  title,
  tasks,
  etiquetas,
  nameOf,
  onChange
}: {
  title: string
  tasks: ActionItem[]
  etiquetas: string[]
  nameOf: (e: string) => string
  onChange: (t: ActionItem[]) => void
}) {
  const [rows, setRows] = useState<ActionItem[]>(tasks)
  const update = (next: ActionItem[]) => {
    setRows(next)
    onChange(next)
  }
  const patch = (i: number, p: Partial<ActionItem>) =>
    update(rows.map((r, j) => (j === i ? { ...r, ...p } : r)))
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <span>{title}</span>
      {rows.map((t, i) => (
        <div key={i} className="row wrap" style={{ gap: 6 }}>
          <input
            className="grow"
            aria-label="Tarea"
            value={t.descripcion}
            onChange={(e) => patch(i, { descripcion: e.target.value })}
            style={{ minWidth: 180 }}
          />
          <select
            aria-label="Responsable"
            value={t.responsableEtiqueta ?? ''}
            onChange={(e) =>
              patch(i, { responsableEtiqueta: e.target.value || null, responsableMencionado: null })
            }
          >
            <option value="">Sin responsable</option>
            {etiquetas.map((e) => (
              <option key={e} value={e}>
                {nameOf(e)}
              </option>
            ))}
          </select>
          <input
            aria-label="Fecha"
            placeholder="Fecha"
            value={t.fecha ?? ''}
            onChange={(e) => patch(i, { fecha: e.target.value || null })}
            style={{ width: 110 }}
          />
          <button
            className="iconbtn"
            aria-label="Quitar tarea"
            onClick={() => update(rows.filter((_, j) => j !== i))}
          >
            <IconClose />
          </button>
        </div>
      ))}
      <div>
        <button
          className="btn sm ghost"
          onClick={() =>
            update([
              ...rows,
              {
                descripcion: '',
                responsableEtiqueta: null,
                responsableMencionado: null,
                fecha: null,
                evidencia: []
              }
            ])
          }
        >
          + Tarea
        </button>
      </div>
    </fieldset>
  )
}
